import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FilterSelect from '@/components/ui/filter-select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/contexts/ApiContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';

// Types
interface PurchaseOrderUpdateFormData {
  status: 'pending' | 'approved' | 'ordered' | 'received' | 'cancelled';
  notes?: string;
}

interface PurchaseOrderUpdateSheetProps {
  purchaseOrder: {
    _id: string;
    purchaseOrderNumber: string;
    status: string;
    notes?: string;
    supplier?: {
      businessName: string;
      contactName: string;
      email?: string;
      contactNumber?: string;
    };
    items?: Array<{
      inventory: { name: string; measurementUnit?: string };
      quantity: number;
      unitPrice: number;
    }>;
    totalAmount?: number;
    expectedDeliveryDate?: string;
    createdAt?: string;
  };
  onPurchaseOrderUpdated?: () => Promise<void>;
}

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PurchaseOrderUpdateSheet: React.FC<PurchaseOrderUpdateSheetProps> = ({
  purchaseOrder,
  onPurchaseOrderUpdated,
}) => {
  const api = useApi();
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, watch, reset } =
    useForm<PurchaseOrderUpdateFormData>({
      defaultValues: {
        status: purchaseOrder.status as any,
        notes: purchaseOrder.notes || '',
      },
      mode: 'onChange',
    });

  const watchedStatus = watch('status');

  const handleSubmitForm = async (formData: PurchaseOrderUpdateFormData) => {
    setIsLoading(true);
    try {
      const response = await api.put(
        `/purchase-order/${purchaseOrder._id}`,
        formData
      );

      if (response.success) {
        showSuccess(response.message || 'Purchase order updated successfully');
        hideSheet('edit-purchase-order');

        // Refresh the purchase orders list if callback provided
        if (onPurchaseOrderUpdated) {
          await onPurchaseOrderUpdated();
        }
      } else {
        showError(response.error?.message || 'Failed to update purchase order');
      }
    } catch (error) {
      showError('An error occurred while updating the purchase order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    hideSheet('edit-purchase-order');
  };

  // Expose handlers to global window object for GlobalSheetRenderer
  useEffect(() => {
    const handlers = {
      handleSubmit: () => handleSubmit(handleSubmitForm)(),
      handleCancel,
      isLoading,
    };

    // Store handlers globally
    if (!(window as any).purchaseOrderUpdateHandlers) {
      (window as any).purchaseOrderUpdateHandlers = {};
    }
    (window as any).purchaseOrderUpdateHandlers = handlers;

    // Cleanup on unmount
    return () => {
      if ((window as any).purchaseOrderUpdateHandlers) {
        delete (window as any).purchaseOrderUpdateHandlers;
      }
    };
  }, [isLoading, handleSubmit, handleSubmitForm]);

  const onSubmit = async (formData: PurchaseOrderUpdateFormData) => {
    await handleSubmitForm(formData);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-warning-60 bg-warning-10';
      case 'approved':
        return 'text-brand-60 bg-brand-10';
      case 'ordered':
        return 'text-purple-60 bg-purple-10';
      case 'received':
        return 'text-success-60 bg-success-10';
      case 'cancelled':
        return 'text-destructive-60 bg-destructive-10';
      default:
        return 'text-gray-60 bg-gray-10';
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Awaiting approval';
      case 'approved':
        return 'Approved and ready to order';
      case 'ordered':
        return 'Order placed with supplier';
      case 'received':
        return 'Goods received and processed';
      case 'cancelled':
        return 'Purchase order cancelled';
      default:
        return '';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className='space-y-6'>
      {/* Purchase Order Information */}
      <div className='bg-gray-5 p-4 rounded-lg'>
        <h4 className='text-sm font-medium text-gray-90 mb-3'>
          Purchase Order Information
        </h4>
        <div className='space-y-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-gray-60'>PO Number:</span>
            <span className='font-mono font-semibold'>
              {purchaseOrder.purchaseOrderNumber}
            </span>
          </div>
          {purchaseOrder.supplier && (
            <>
              <div className='flex justify-between'>
                <span className='text-gray-60'>Supplier:</span>
                <span className='font-medium'>
                  {purchaseOrder.supplier.businessName}
                </span>
              </div>
              {purchaseOrder.supplier.contactName && (
                <div className='flex justify-between'>
                  <span className='text-gray-60'>Contact Person:</span>
                  <span>{purchaseOrder.supplier.contactName}</span>
                </div>
              )}
              {purchaseOrder.supplier.email && (
                <div className='flex justify-between'>
                  <span className='text-gray-60'>Email:</span>
                  <span>{purchaseOrder.supplier.email}</span>
                </div>
              )}
              {purchaseOrder.supplier.contactNumber && (
                <div className='flex justify-between'>
                  <span className='text-gray-60'>Phone:</span>
                  <span>{purchaseOrder.supplier.contactNumber}</span>
                </div>
              )}
            </>
          )}
          <div className='flex justify-between'>
            <span className='text-gray-60'>Items:</span>
            <span>{purchaseOrder.items?.length || 0} items</span>
          </div>
          {purchaseOrder.expectedDeliveryDate && (
            <div className='flex justify-between'>
              <span className='text-gray-60'>Expected Delivery:</span>
              <span>{formatDate(purchaseOrder.expectedDeliveryDate)}</span>
            </div>
          )}
          {purchaseOrder.createdAt && (
            <div className='flex justify-between'>
              <span className='text-gray-60'>Created:</span>
              <span>{formatDate(purchaseOrder.createdAt)}</span>
            </div>
          )}
          {purchaseOrder.totalAmount && (
            <div className='flex justify-between font-semibold border-t pt-2'>
              <span>Total Amount:</span>
              <span>₦{purchaseOrder.totalAmount.toLocaleString()}</span>
            </div>
          )}
          <div className='flex justify-between items-center pt-2 border-t'>
            <span className='text-gray-60'>Current Status:</span>
            <div className='text-right'>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(purchaseOrder.status)}`}
              >
                {purchaseOrder.status.charAt(0).toUpperCase() +
                  purchaseOrder.status.slice(1)}
              </span>
              <p className='text-xs text-gray-50 mt-1'>
                {getStatusDescription(purchaseOrder.status)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Order Items */}
      {purchaseOrder.items && purchaseOrder.items.length > 0 && (
        <div className='bg-gray-5 p-4 rounded-lg'>
          <h4 className='text-sm font-medium text-gray-90 mb-3'>
            Ordered Items ({purchaseOrder.items.length})
          </h4>
          <div className='space-y-2'>
            {purchaseOrder.items.map((item, index) => (
              <div key={index} className='text-sm bg-white p-3 rounded border'>
                <div className='flex justify-between items-start'>
                  <div className='flex-1'>
                    <p className='font-medium text-gray-90'>
                      {item.inventory.name}
                    </p>
                    <p className='text-gray-60'>
                      Quantity: {item.quantity.toLocaleString()}
                      {item.inventory.measurementUnit &&
                        ` ${item.inventory.measurementUnit}`}
                    </p>
                  </div>
                  <div className='text-right'>
                    <p className='font-medium'>
                      ₦{item.unitPrice.toLocaleString()}
                    </p>
                    <p className='text-gray-60 text-xs'>per unit</p>
                  </div>
                </div>
                <div className='flex justify-between items-center mt-2 pt-2 border-t border-gray-10'>
                  <span className='text-gray-60'>Subtotal:</span>
                  <span className='font-semibold'>
                    ₦{(item.quantity * item.unitPrice).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        {/* Status Update */}
        <div className='space-y-2'>
          <Label htmlFor='status'>Update Purchase Order Status *</Label>
          <Controller
            name='status'
            control={control}
            rules={{ required: 'Status is required' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                name='status'
                value={field.value}
                options={statusOptions}
                placeholder='Select purchase order status'
                error={fieldState?.error?.message}
                onChange={field.onChange}
                className='w-full'
              />
            )}
          />
          {watchedStatus && (
            <p className='text-sm text-gray-60'>
              {getStatusDescription(watchedStatus)}
            </p>
          )}
        </div>

        {/* Notes */}
        <div className='space-y-2'>
          <Label htmlFor='notes'>Update Notes (Optional)</Label>
          <Controller
            name='notes'
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                placeholder='Add any notes about this status update, delivery information, or other relevant details'
                rows={4}
              />
            )}
          />
          <p className='text-xs text-gray-50'>
            These notes will be added to the purchase order history
          </p>
        </div>

        {/* Status Change Warning */}
        {watchedStatus === 'cancelled' && (
          <div className='bg-destructive-5 border border-destructive-20 rounded-lg p-3'>
            <p className='text-sm text-destructive-70'>
              <strong>Warning:</strong> Cancelling this purchase order cannot be
              undone. Please ensure this is the correct action.
            </p>
          </div>
        )}

        {watchedStatus === 'received' && (
          <div className='bg-success-5 border border-success-20 rounded-lg p-3'>
            <p className='text-sm text-success-70'>
              <strong>Note:</strong> Marking as received will update inventory
              levels if configured.
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default PurchaseOrderUpdateSheet;
